import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import HubOperatorLayout from "@/components/HubOperatorLayout";
import InwardScanner from "@/components/pharmeasy/InwardScanner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Package, Loader2, ScanBarcode, RefreshCw, MapPin, TrendingUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HubOperatorInProgress = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/inward/operator/sessions`, {
        params: { status: "IN_PROGRESS" }
      });
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.error("Failed to fetch sessions:", e);
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleContinueInward = (session) => {
    setSelectedSession(session);
    setScannerOpen(true);
  };

  const handleScanSuccess = (result) => {
    // Refresh the session data
    fetchSessions(true);
    toast.success(`Inwarded to ${result.location_name} / ${result.sublocation_name}`);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-IN", { 
        day: "numeric", 
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return iso;
    }
  };

  const getProgressPercent = (session) => {
    const inwarded = session.inwarded_shipments?.length || session.inwarded_count || 0;
    const total = session.total_shipments || 1;
    return Math.round((inwarded / total) * 100);
  };

  if (loading) {
    return (
      <HubOperatorLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </HubOperatorLayout>
    );
  }

  return (
    <HubOperatorLayout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="hub-operator-in-progress">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800">In Progress</h1>
            <p className="text-sm text-slate-500">{sessions.length} active session(s)</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchSessions(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-12 text-center">
              <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No sessions in progress</p>
              <p className="text-sm text-slate-400 mt-1">Start scanning parcels from a pending session</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => {
              const inwarded = session.inwarded_shipments?.length || session.inwarded_count || 0;
              const total = session.total_shipments || 0;
              const progress = getProgressPercent(session);
              
              return (
                <Card key={session._id} className="border-0 shadow-md overflow-hidden border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    {/* Session Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-800">{session.session_code}</h3>
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            <Loader2 className="w-3 h-3 mr-1" /> In Progress
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Created {formatDate(session.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-600">Progress</span>
                        <span className="font-semibold text-blue-600">{inwarded} / {total}</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                      <p className="text-xs text-slate-400 mt-1">{progress}% complete</p>
                    </div>

                    {/* Session Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-blue-600">{session.total_trips || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Trips</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-amber-600">{total - inwarded}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Remaining</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2 text-center">
                        <p className="text-lg font-bold text-emerald-600">{inwarded}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Inwarded</p>
                      </div>
                    </div>

                    {/* Hub Info */}
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{session.hub_name || "Unknown Hub"}</span>
                    </div>

                    {/* Action Button */}
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => handleContinueInward(session)}
                      data-testid={`continue-btn-${session.session_code}`}
                    >
                      <ScanBarcode className="w-4 h-4 mr-2" />
                      Continue Inward
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Scanner Modal */}
      {selectedSession && (
        <InwardScanner
          isOpen={scannerOpen}
          onClose={() => {
            setScannerOpen(false);
            fetchSessions(true);
          }}
          sessionId={selectedSession._id}
          hubId={selectedSession.hub_id}
          onScanSuccess={handleScanSuccess}
          sessionData={selectedSession}
        />
      )}
    </HubOperatorLayout>
  );
};

export default HubOperatorInProgress;
